import { useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import DraggableReasonItem, { ReasonRowItem } from "./DraggableReasonItem";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";

const EMPTY_IDS: number[] = [];

interface ReasonListItem {
  id: number;
  name: string;
}

interface Props<T extends ReasonListItem> {
  items: T[];
  onItemsChange: (items: T[]) => void;
  onEdit: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  tPrefix?: string;
  droppableId?: string;
  protectedEditIds?: number[];
  protectedEditTooltip?: string;
  className?: string;
}

const DraggableReasonListContainer = <T extends ReasonListItem>({
  items,
  onItemsChange,
  onEdit,
  onDelete,
  tPrefix = "settings.deals.tabs.generalTab",
  droppableId = "reason-list",
  protectedEditIds = EMPTY_IDS,
  protectedEditTooltip,
  className = ""
}: Props<T>) => {
  const { t } = useTranslation();
  const isSingleItem = items.length <= 1;
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    itemId: number | null;
  }>({
    isOpen: false,
    itemId: null
  });

  const transformedItems: ReasonRowItem[] = items.map((reason) => ({
    id: reason.id.toString(),
    title: reason.name
  }));

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    const newItems = [...items];
    const [removed] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, removed);

    onItemsChange(newItems);
  };

  const handleEdit = (id: string, newTitle: string) => {
    onEdit(id, newTitle);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteModal({
      isOpen: true,
      itemId: parseInt(id, 10)
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.itemId !== null && items.length > 1) {
      await onDelete(deleteModal.itemId.toString());
    }
    setDeleteModal({ isOpen: false, itemId: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, itemId: null });
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={droppableId} isDropDisabled={isSingleItem}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={cn(
                "rounded-2xl border border-border-border1 bg-surface-widget-card overflow-hidden",
                className
              )}
            >
              {transformedItems.map((item, index) => (
                <DraggableReasonItem
                  key={item.id}
                  item={item}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  tPrefix={tPrefix}
                  isDragDisabled={isSingleItem}
                  isLast={index === items.length - 1}
                  shouldDisable={isSingleItem}
                  isProtected={protectedEditIds.includes(parseInt(item.id))}
                  protectedEditTooltip={protectedEditTooltip}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="[&_[data-test='cancel']]:h-8 [&_[data-test='delete']]:h-8 [&_[data-test='close-modal']]:w-8 [&_[data-test='close-modal']]:h-8 [&_[data-test='close-modal']]:scale-100">
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          setIsOpen={(open) => {
            if (!open) {
              setDeleteModal({ isOpen: false, itemId: null });
            }
          }}
          title={t(`${tPrefix}.deleteModal.title`)}
          confirmationText={t(`${tPrefix}.deleteModal.description`)}
          handleSubmit={handleDeleteConfirm}
          handleCancel={handleDeleteCancel}
          hideDivider={true}
        />
      </div>
    </>
  );
};

export default DraggableReasonListContainer;
